import { useGridEditor } from "./GridEditorContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { LayoutGrid, Sparkles, Upload } from "lucide-react";
import { UndoRedoControls } from "../reusable/UndoRedoControls";

export function GridEditorControls() {
    const {
        objectSvg,
        error,
        columns,
        rows,
        cellWidth,
        cellHeight,
        cellSpacingX,
        cellSpacingY,
        density,
        fillPattern,
        startPosition,
        endPosition,
        noise,
        dithering,
        svgInputRef,
        handleFileLoad,
        setColumns,
        setRows,
        setCellWidth,
        setCellHeight,
        setCellSpacingX,
        setCellSpacingY,
        setDensity,
        setFillPattern,
        setStartPosition,
        setEndPosition,
        setNoise,
        setDithering,
        viewBoxAttr,
        applyPreset,
        getPresetValues,
        resetParameters,
        undo,
        redo,
        canUndo,
        canRedo,
    } = useGridEditor();

    const handleColumnsChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 1) return;
        setColumns(Math.max(1, Math.floor(numValue)));
    };

    const handleRowsChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 1) return;
        setRows(Math.max(1, Math.floor(numValue)));
    };

    const handleCellWidthChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 1) return;
        setCellWidth(Math.max(1, numValue));
    };

    const handleCellHeightChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 1) return;
        setCellHeight(Math.max(1, numValue));
    };

    const handleCellSpacingXChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setCellSpacingX(numValue);
    };

    const handleCellSpacingYChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setCellSpacingY(numValue);
    };

    const handleDensityChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setDensity(Math.max(0, Math.min(100, numValue)));
    };

    const handleNoiseChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setNoise(Math.max(0, Math.min(100, numValue)));
    };

    const handleDitheringChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setDithering(Math.max(0, Math.min(100, numValue)));
    };

    return (
        <div className="panel-controls grid-editor-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Grid Editor</h2>
                    <ToolDescriptionPopover dialogLabel="Grid Editor tool description">
                        <p>
                            Import an SVG object and edit it by dragging
                            elements or using copy-paste (Ctrl+C, Ctrl+V). The
                            grid updates to show the repeated pattern.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="grid-editor"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Source" icon={Upload}>
                    <label className="field-label">Object SVG File</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => svgInputRef.current?.click()}
                        >
                            Choose SVG file...
                        </button>
                        <input
                            ref={svgInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleFileLoad}
                        />
                    </div>
                    {objectSvg && (
                        <p className="upload-info">
                            File: <strong>{objectSvg.name}</strong>
                            {viewBoxAttr && (
                                <>
                                    {" "}
                                    - viewBox: <code>{viewBoxAttr}</code>
                                </>
                            )}
                        </p>
                    )}
                    {!objectSvg && (
                        <p className="upload-hint">
                            Upload an SVG file to begin editing. Drag elements or
                            use Ctrl+C / Ctrl+V to copy and paste.
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Grid parameters" icon={LayoutGrid}>
                    <div className="field-row">
                        <label className="field-label">
                            Columns
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={columns}
                                onChange={handleColumnsChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rows
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={rows}
                                onChange={handleRowsChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Cell Width
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={cellWidth}
                                onChange={handleCellWidthChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Cell Height
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={cellHeight}
                                onChange={handleCellHeightChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Cell Spacing (Horizontal)
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={cellSpacingX}
                                onChange={handleCellSpacingXChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Cell Spacing (Vertical)
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={cellSpacingY}
                                onChange={handleCellSpacingYChange}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Distribution" icon={Sparkles}>
                    <div className="field-row">
                        <label className="field-label">
                            Density (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={density}
                                onChange={handleDensityChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Fill Pattern
                            <select
                                value={fillPattern}
                                onChange={(e) =>
                                    setFillPattern(
                                        e.target.value as
                                            | "even"
                                            | "linear-gradient"
                                            | "radial-gradient"
                                            | "reverse-radial-gradient"
                                            | "perlin-noise",
                                    )
                                }
                                className="curve-shape-select"
                            >
                                <option value="even">Even</option>
                                <option value="linear-gradient">
                                    Linear Gradient
                                </option>
                                <option value="radial-gradient">
                                    Radial Gradient
                                </option>
                                <option value="reverse-radial-gradient">
                                    Reverse Radial Gradient
                                </option>
                                <option value="perlin-noise">
                                    Perlin Noise
                                </option>
                            </select>
                        </label>
                    </div>
                    {fillPattern === "linear-gradient" && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Start Position
                                    <select
                                        value={startPosition}
                                        onChange={(e) =>
                                            setStartPosition(e.target.value)
                                        }
                                        className="curve-shape-select"
                                    >
                                        <option value="top-left">
                                            Top Left
                                        </option>
                                        <option value="top">Top</option>
                                        <option value="top-right">
                                            Top Right
                                        </option>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                        <option value="bottom-left">
                                            Bottom Left
                                        </option>
                                        <option value="bottom">Bottom</option>
                                        <option value="bottom-right">
                                            Bottom Right
                                        </option>
                                    </select>
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    End Position
                                    <select
                                        value={endPosition}
                                        onChange={(e) =>
                                            setEndPosition(e.target.value)
                                        }
                                        className="curve-shape-select"
                                    >
                                        <option value="top-left">
                                            Top Left
                                        </option>
                                        <option value="top">Top</option>
                                        <option value="top-right">
                                            Top Right
                                        </option>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                        <option value="bottom-left">
                                            Bottom Left
                                        </option>
                                        <option value="bottom">Bottom</option>
                                        <option value="bottom-right">
                                            Bottom Right
                                        </option>
                                    </select>
                                </label>
                            </div>
                        </>
                    )}
                    {(fillPattern === "radial-gradient" ||
                        fillPattern === "reverse-radial-gradient") && (
                        <div className="field-row">
                            <label className="field-label">
                                Start Position
                                <select
                                    value={startPosition}
                                    onChange={(e) =>
                                        setStartPosition(e.target.value)
                                    }
                                    className="curve-shape-select"
                                >
                                    <option value="top-left">Top Left</option>
                                    <option value="top">Top</option>
                                    <option value="top-right">Top Right</option>
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                    <option value="bottom-left">
                                        Bottom Left
                                    </option>
                                    <option value="bottom">Bottom</option>
                                    <option value="bottom-right">
                                        Bottom Right
                                    </option>
                                </select>
                            </label>
                        </div>
                    )}
                    <div className="field-row">
                        <label className="field-label">
                            Noise (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={noise}
                                onChange={handleNoiseChange}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Dithering (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={dithering}
                                onChange={handleDitheringChange}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>
            </div>

            {error && <p className="upload-error">{error}</p>}
        </div>
    );
}
